import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Divider, IconButton, Stack } from '@mui/material';
import { DRAWER_WIDTH, NARROW_DRAWER_WIDTH } from 'components/common/nav-drawer';
import { NotificationBell } from 'components/common/notification-bell';
import { useOutreachActivityVisible } from 'hooks/use-outreach-activity-visible';
import { useAuthContext } from 'providers/auth';
import { usePageContext } from 'providers/page';
import { FC } from 'react';
import { useMatch } from 'react-router';
import routes from 'routes';

interface AppBarProps {
  handleDrawerToggle: () => void;
  handleLogout: () => void;
  handleSearchToggle: () => void;
}

const TITLE_TOGGLE_INSET = 3;

export const AppBar: FC<AppBarProps> = ({ handleDrawerToggle, handleSearchToggle }) => {
  const authContext = useAuthContext();
  const { sidebarCollapsed, leftNavDrawerOpen } = usePageContext();
  // Shared with OutreachActivityProvider so the bell and the feed behind it can't be gated apart.
  const activityVisible = useOutreachActivityVisible();
  // The Companies page renders its own search in its toolbar.
  const onCompaniesPage = !!useMatch(`/${routes.home.companies.path}`);

  return (
    <Stack direction="column" height={128}>
      <Box
        display="flex"
        height={128}
        sx={{
          marginLeft: {
            xs: 0,
            md: sidebarCollapsed ? `${NARROW_DRAWER_WIDTH}px` : `${DRAWER_WIDTH}px`,
          },
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: (theme) => theme.zIndex.drawer - 1,
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          {/* Mobile-only: opens the overlay. Desktop's toggle lives in the drawer. */}
          <IconButton
            color="inherit"
            aria-label="Open navigation"
            aria-expanded={leftNavDrawerOpen}
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
          {/* Inset the title to clear the toggle; tabs sit lower, so cancel it for them. */}
          <Box
            ml={2}
            id="portal-header-title"
            sx={(theme) => ({
              pl: TITLE_TOGGLE_INSET,
              '& > .navigable-tabs-root': {
                transform: `translateX(-${theme.spacing(TITLE_TOGGLE_INSET)})`,
              },
            })}
          />
        </Stack>

        {/* pr is load-bearing: MUI hangs a Badge half its width outside the icon, so flush against
            the viewport edge a two-digit count overflowed the document and grew a scrollbar. */}
        <Stack direction="row" spacing={2} sx={{ pr: 2 }}>
          {activityVisible && <NotificationBell />}
          {authContext.isInternal && !onCompaniesPage && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="search"
              onClick={handleSearchToggle}
            >
              <SearchIcon />
            </IconButton>
          )}
        </Stack>
      </Box>
      <Divider />
    </Stack>
  );
};
